import { matchPath } from 'react-router';

import { APP_NAME } from 'src/common/constants';
import {
  NAMESPACE_ROUTE,
  SEARCH_PATH,
  STRING_GROUP_ROUTE,
  STRING_ROUTE,
  TRACK_MY_TRANSLATIONS_PATH,
} from 'src/common/routes';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** A page's screen-reader announcement text and full document title. */
export interface DocumentTitle {
  /**
   * Short, human page name for the live-region announcement (e.g. 'Search',
   * 'Namespace Retail_Website'). Empty string for the home/root shell, where the
   * bare product name is the whole title and there is nothing extra to announce.
   */
  readonly label: string;
  /** Value for `document.title` — `'<label> · <APP_NAME>'`, or just APP_NAME. */
  readonly title: string;
}

/**
 * Decode a percent-encoded route id for display. Ids in the URL are always
 * percent-encoded (see src/common/routes.ts); a malformed sequence (a literal,
 * unescaped '%' a user typed into the address bar) makes decodeURIComponent
 * throw, so fall back to the raw segment rather than blanking the title.
 */
const decodeId = (raw: string): string => {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/** Ordered most-specific first: STRING is a superpath of GROUP is a superpath of NAMESPACE. */
const withParam = (
  pattern: string,
  param: string,
  makeLabel: (t: TranslateFn, value: string) => string,
): ((t: TranslateFn, pathname: string) => string | null) => {
  return (t: TranslateFn, pathname: string) => {
    const match = matchPath(pattern, pathname);
    const value = match?.params?.[param];
    return value ? makeLabel(t, decodeId(value)) : null;
  };
};

const staticMatch = (
  pattern: string,
  labelId: string,
): ((t: TranslateFn, pathname: string) => string | null) => {
  return (t: TranslateFn, pathname: string) =>
    matchPath(pattern, pathname) ? t(labelId) : null;
};

/**
 * Label resolvers tried in order; the first non-null wins. Dynamic (id-bearing)
 * routes come before their static ancestors so the most specific page name is
 * chosen. Anything unmatched (home, root, unknown) yields no label.
 */
const LABEL_RESOLVERS: ReadonlyArray<
  (t: TranslateFn, pathname: string) => string | null
> = [
  withParam(STRING_ROUTE, 'stringId', (t, id) => t('doc-title-string', { id })),
  withParam(STRING_GROUP_ROUTE, 'stringGroupId', (t, id) =>
    t('doc-title-string-group', { id }),
  ),
  withParam(NAMESPACE_ROUTE, 'namespaceId', (t, id) =>
    t('doc-title-namespace', { id }),
  ),
  staticMatch(SEARCH_PATH, 'doc-title-search'),
  staticMatch(TRACK_MY_TRANSLATIONS_PATH, 'doc-title-track'),
];

/**
 * Map a router pathname to its document title and announcement label. Pure and
 * data-free: dynamic pages are titled from the URL id, not the loaded entity, so
 * this never waits on a query. A page may still refine its own title later.
 */
export const deriveDocumentTitle = (
  t: TranslateFn,
  pathname: string,
): DocumentTitle => {
  for (const resolve of LABEL_RESOLVERS) {
    const label = resolve(t, pathname);
    if (label) {
      // One resource rather than `label + separator + APP_NAME`: the separator and
      // the order of the two halves differ by locale, so joining them here would
      // hand translators two fragments and no way to reorder them. Both values are
      // untranslated parameters — `label` is already localized, `appName` is the
      // product name.
      return {
        label,
        title: t('doc-title-frame', { label, appName: APP_NAME }),
      };
    }
  }
  return { label: '', title: APP_NAME };
};
